import { Chip } from '@mui/material';
import { useLocation } from 'react-router-dom';

/**
 * Marks any non-production build so it cannot be mistaken for the live site.
 *
 * This matters more here than in a typical setup: the staging database is a
 * copy of production, real names and phone numbers included, and the interface
 * is identical. The confusion is dangerous both ways — deleting something
 * "because it is only staging", or dismissing a real production fault as a test
 * environment.
 *
 * Renders ONLY when VITE_APP_ENV says something other than "production". An
 * unset variable shows nothing, deliberately: the failure that would actually
 * hurt is a customer seeing STAGING stamped on the live site, so this stays
 * silent unless a build positively declares it is not production. Vite inlines
 * the value at build time, so it is fixed per deployment and cannot be flipped
 * from the browser.
 */
const environment = import.meta.env.VITE_APP_ENV as string | undefined;

export const isNonProduction = Boolean(environment) && environment !== 'production';

/**
 * Inline chip for the dashboard navbar.
 *
 * Sits in the navbar's Stack rather than pinned to the viewport, so it cannot
 * cover the account menu and it moves with the impersonation banner's offset
 * for free — the navbar already applies that, and a fixed badge would have
 * needed its own copy of the logic.
 */
export function EnvironmentChip() {
  if (!isNonProduction) return null;

  return (
    <Chip
      label={environment}
      size="small"
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: '0.65rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        bgcolor: 'grey.900',
        color: 'common.white',
        // Not the warning colour: that belongs to the impersonation banner, and
        // two meanings sharing one colour is how a warning stops being read.
        border: '2px solid #f9d608',
      }}
    />
  );
}

/**
 * Fixed badge for pages with no navbar — login above all.
 *
 * Signing in is the moment the environment matters most, because that is when
 * real credentials get typed, and the auth routes render without a layout to
 * hang a chip on. Suppressed under /dashboard so it never doubles up with the
 * navbar chip.
 */
export default function EnvironmentBadge() {
  const { pathname } = useLocation();

  if (!isNonProduction) return null;
  if (pathname.startsWith('/dashboard')) return null;

  return (
    <Chip
      label={environment}
      size="small"
      aria-hidden
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        // Below the impersonation banner (9999) so the two never fight.
        zIndex: 9998,
        height: 24,
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        bgcolor: 'grey.900',
        color: 'common.white',
        border: '2px solid #f9d608',
        boxShadow: 3,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    />
  );
}
